import * as actions_invoices from 'actions/invoices-actions';
import * as actions_quickbooks from 'actions/quickbooks-actions';
import * as toolbox from 'components/common/toolbox';
import React, { useEffect, useState } from 'react';
import StatusKey from 'components/common/status-key';
import { Ibox, ModalAlert } from 'enspire-manager-framework';
import { PAYMENTS, PERMISSIONS, SERVICE_REQUEST_TERMS } from 'components/constants';
import { Table } from 'em-table';
import { numPanels } from 'components/common/toolbox';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useParams } from 'react-router-dom';

var _ = require('lodash');

export default function Payments(props) {

    /* Hooks ------------------------------------------------------------------------------------------------------------------------------------*/

    const history = useHistory();
    const routeLocation = useLocation();
    const dispatch = useDispatch();
    const params = useParams();

    const invoices = useSelector((store) => store.invoices);
    const quickbooksSettings = useSelector((store) => store.quickbooks.quickbooks);
    const quotes = useSelector((store) => store.quotes);
    const serviceRequest = useSelector((store) => store.serviceRequests.service_request);;
    const taxRates = useSelector((store) => store.settings.settings?.invoices?.tax_rates?.filter((rate) => !rate.hidden));
    const user_permissions = useSelector((store) => store.users?.user_permissions ?? {});
    
    const [timer, setTimer] = useState(null);
    const [canAuto, setCanAuto] = useState(true);
    
    const payments = invoices?.payments;
    const seg = (props?.source == 'customers') ? 7 : (props?.source == 'invoices') ? ((params.request_id) ? 12 : 10) : 9;
    const segments = props.location.pathname.split('/').length;

    /* Effects ------------------------------------------------------------------------------------------------------------------------------------*/

    // Subscribe by profileId for Customer Dashboard, requestId for Service Request, invoiceId for Invoice
    useEffect(() => {
        dispatch(actions_invoices.subPaymentsByField(params.handle,
            (props?.source == 'service-requests')
                ? 'requestId'
                : 'customerId',
            (props?.source == 'service-requests')
                ? params.request_id
                : params.profile_id
        ));

        if (props?.source == 'service-requests') {
            dispatch(actions_invoices.subInvoicesByField(params.handle, 'requestId', params.request_id));
        }

        return () => {
            dispatch(actions_invoices.clearPayments());

            var unsubscribe = invoices.payments_unsubscribe;
            if (typeof unsubscribe === 'function') unsubscribe();

            var unsubscribe = invoices.invoices_unsubscribe;
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [params.customer_id, params.request_id, params.invoice_id]);

    useEffect(() => { setCanAuto(true); }, [params.tab]);

    // Automatically advance to the first item
    useEffect(() => {
        if (numPanels(3) && canAuto && params.tab == 'payments' && payments?.length && segments < seg+2) {
            if (timer) clearTimeout(timer);
            setTimer(setTimeout(() => {
                let location = toolbox.modifyPath(props.location.pathname, seg, 'payments/preview/' + payments[0]?.id);
                props.history.push({ pathname: location });
            }, 300));
        }
        setTimeout(() => { setCanAuto(false) }, 2000);
    }, [params.tab, payments]);

    /* Handlers ------------------------------------------------------------------------------------------------------------------------------------*/

    const handleButton = () => {
        let newLocation = toolbox.modifyPath(props.location.pathname, 5, 'payment-form/0');
        props.history.push({ pathname: newLocation });
    };
    const handleClick = (invoice) => {
        let startSegment = (serviceRequest.serviceRequestTermsId == SERVICE_REQUEST_TERMS.SERVICE_CALL.id) ? seg+3 : seg+1;
        const changeHistory = (routeLocation.pathname.includes('preview'))
            ? history.replace
            : history.push;
        let location = toolbox.modifyPath(routeLocation.pathname, startSegment, 'preview/' + invoice.id);
        changeHistory({ pathname: location });
    };
    const confirmDelete = (row) => {
        ModalAlert({
            title: 'Are you sure you want to archive this Payment?',
            text: "This Payment will be Archived! It can be restored or deleted permanently by visiting the Archive page.",
            icon: 'question',
            confirm_color: '#8FBC8B',
            confirm_text: 'Yes',
            deny_text: 'No, take me back',
            show_deny: true,
            callback_success: () => deletePayment(row)
        });
    };
    const deletePayment = (row) => {
        dispatch(actions_invoices.deletePayment(params.handle, row.id, taxRates));
        dispatch(actions_quickbooks.updateQuickbooks({ handle: params.handle, type: 'payment' }, quickbooksSettings));
    };

    /* Constants ------------------------------------------------------------------------------------------------------------------------------------*/
    
    var data = payments?.map((payment) => ({ 
        ...payment, 
        _stripe_color: Object.values(PAYMENTS).find((item) => item.id == payment.statusId)?.color,
        method: `${payment.method} ${(payment.checkNumber) ? '#' + payment.checkNumber : ''}`,
        memo: 'INVOICES ' + payment.invoices?.map((invoice) => `#${invoice.id} - ${toolbox.formatNumber(invoice.amount, 2, true)}`).join(', '),
        status: 'Closed', 
    }));

    if (props.source == 'invoices') data = data?.filter((payment) => payment.invoices.find((invoice) => invoice.id == params.invoice_id));
    if (props.source == 'service-requests') data = data?.filter((payment) => payment.requestId == params.request_id);
    
    const redistWidth = 45;
    const redist = (num) => (numPanels() > 1) ? num : num + (num / (100 - redistWidth) * (redistWidth));
    const columns = [
        { name: 'ID', field: 'id', width: redist(10) },
        { name: 'Date', field: 'date', type: 'date', format: 'MMM Do, YYYY', width: redist(20) },
        { name: 'Memo', field: 'memo', ...(!numPanels(1)) ? { width: 45 } : {} },
        { name: 'Method', field: 'method', width: redist(10) },
        { name: 'Total', field: 'amount', type: 'number', format: 'usd', text_align: 'right', width: redist(15) },
    ];

    /* Render ------------------------------------------------------------------------------------------------------------------------------------*/

    return (
        <>
            <Ibox title={'Payments'} 
                show_spinner={quotes.quotes_pending} 
                no_fade={false}
                button={' + Payment'}
                button_disabled={(!user_permissions?.[PERMISSIONS.PAYMENTS_RECEIVE.key])}
                button_callback={handleButton}
            >
                <Table
                    active_field={'id'}
                    active_id={params.payment_id}
                    click_callback={handleClick}
                    columns={columns}
                    data={data}
                    delete={(user_permissions.PAYMENTS_DELETE) && true}
                    highlight_search={true}
                    max_height={'135px'}
                    on_delete={confirmDelete}
                    second_line={(numPanels(1)) ? 'memo' : ''}
                    second_line_prefix={<i className="fa-solid fa-circle-arrow-right fa-lg mr-1"></i>}
                    show_limit={false}
                    show_search={true}
                />
            </Ibox>
        </>

    );
};